/**
 * Web Vitals Monitoring Service
 * 
 * Tracks Core Web Vitals for performance monitoring:
 * - LCP (Largest Contentful Paint) - Loading performance
 * - FID (First Input Delay) - Interactivity
 * - CLS (Cumulative Layout Shift) - Visual stability
 * - FCP (First Contentful Paint) - Initial rendering
 * - TTFB (Time to First Byte) - Server response
 * 
 * Good scores:
 * - LCP: < 2.5s
 * - FID: < 100ms
 * - CLS: < 0.1
 * - FCP: < 1.8s
 * - TTFB: < 600ms
 */

import { onCLS, onINP, onFCP, onLCP, onTTFB, type Metric } from 'web-vitals';

export interface WebVitalsReport {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  navigationType: string;
}

function getRating(metric: Metric): 'good' | 'needs-improvement' | 'poor' {
  const thresholds: Record<string, [number, number]> = {
    CLS: [0.1, 0.25],
    FID: [100, 300],
    LCP: [2500, 4000],
    FCP: [1800, 3000],
    TTFB: [800, 1800],
  };

  const [good, poor] = thresholds[metric.name] || [0, Infinity];
  
  if (metric.value <= good) return 'good';
  if (metric.value <= poor) return 'needs-improvement';
  return 'poor';
}

function logMetric(metric: Metric) {
  const rating = getRating(metric);
  const report: WebVitalsReport = {
    name: metric.name,
    value: Math.round(metric.value),
    rating,
    navigationType: metric.navigationType,
  };

  // Console log with color coding
  const color = rating === 'good' ? '#10b981' : rating === 'needs-improvement' ? '#f59e0b' : '#ef4444';
  console.log(
    `%c📊 ${report.name}: ${report.value}${getUnit(report.name)} (${report.rating})`,
    `color: ${color}; font-weight: bold;`
  );

  // Send to analytics (if available)
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', metric.name, {
      event_category: 'Web Vitals',
      value: Math.round(metric.value),
      event_label: metric.id,
      non_interaction: true,
    });
  }

  // Send to Sentry (if available - will be added in next phase)
  if (typeof window !== 'undefined' && (window as any).Sentry) {
    (window as any).Sentry.captureMessage(`Web Vital: ${metric.name}`, {
      level: rating === 'poor' ? 'warning' : 'info',
      tags: {
        web_vital: metric.name,
        rating: rating,
      },
      extra: {
        value: metric.value,
        navigationType: metric.navigationType,
      },
    });
  }
}

function getUnit(metricName: string): string {
  if (metricName === 'CLS') return '';
  if (metricName === 'TTFB' || metricName === 'FCP' || metricName === 'LCP') return 'ms';
  if (metricName === 'FID') return 'ms';
  return '';
}

/**
 * Initialize Web Vitals monitoring
 * Call this once in your app's entry point (main.tsx)
 * 
 * @param onReport Optional callback to receive metric reports (e.g., for Sentry)
 */
export function reportWebVitals(onReport?: (metric: Metric) => void) {
  // Only run in browser environment
  if (typeof window === 'undefined') return;

  const reporter = (metric: Metric) => {
    logMetric(metric);
    onReport?.(metric);
  };

  onCLS(reporter);
  onINP(reporter);
  onFCP(reporter);
  onLCP(reporter);
  onTTFB(reporter);

  console.log(
    '%c📊 Web Vitals Monitoring Active',
    'color: #10b981; font-weight: bold; font-size: 12px;'
  );
}
